import { useMutation, useQuery, useQueryClient } from 'react-query';
import { subscriptionService } from '../services/subscription';
import {
  BillingPlansResponse,
  CreateTrialCheckoutPayload,
  SubscriptionStatus,
} from '../types';

export const useSubscription = () => {
  const queryClient = useQueryClient();
  const { data: status, isLoading, error } = useQuery<SubscriptionStatus>(
    'subscription-status',
    subscriptionService.getStatus,
    {
      refetchInterval: 60000,
      retry: 1,
    },
  );

  const createCheckout = useMutation(
    (payload: CreateTrialCheckoutPayload | number) => {
      // Backwards-compatible call signature: bare bundle_years number
      // (used by old code paths) or the new payload object.
      const body: CreateTrialCheckoutPayload =
        typeof payload === 'number' ? { bundle_years: payload } : payload;
      return subscriptionService.createTrialCheckout(body);
    },
    {
      onSuccess: (data) => {
        window.location.href = data.checkout_url;
      },
    },
  );

  // Change an existing subscription in place — refetch status, no redirect.
  const changePlan = useMutation(
    (payload: CreateTrialCheckoutPayload) => subscriptionService.changePlan(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('subscription-status');
      },
    },
  );

  return {
    status,
    isLoading,
    error,
    createCheckout: createCheckout.mutate,
    isCreatingCheckout: createCheckout.isLoading,
    changePlan: changePlan.mutateAsync,
    isChangingPlan: changePlan.isLoading,
  };
};

export const useBillingPlans = () => {
  return useQuery<BillingPlansResponse>(
    'billing-plans',
    subscriptionService.getPlans,
    {
      // Plan catalog rarely changes; cache aggressively.
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  );
};
